import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"
import Badge      from "@react/shared/ui/Badge"
import AvenantModal from "./AvenantModal"
import { signAvenant, cancelAvenant, destroyAvenant, submitAvenant, refuseAvenant } from "../api/avenants"
import { usePanorama } from "../context/PanoramaContext"
import { fmt, dateFmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const STATUS_TONE = {
  draft:     "neutral",
  pending:   "warning",
  signed:    "success",
  refused:   "danger",
  cancelled: "neutral",
}

const COLUMNS = "grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_110px_70px_120px_110px_130px] gap-3"

const Totals = ({ signedTotal, signedCount, pendingTotal, pendingCount, revisedContract }) => (
  <div className="flex flex-wrap items-stretch gap-3">
    <div className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">
        {I18n.t("projects.avenants.summary.signed_total")}
      </div>
      <div className="text-[18px] font-semibold tabular-nums text-slate-900">{fmt(signedTotal)}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">
        {I18n.t("projects.avenants.summary.signed_count", { count: signedCount })}
      </div>
    </div>

    <div className="px-4 py-3 bg-amber-50/70 border border-amber-200 rounded-xl">
      <div className="text-[10.5px] uppercase tracking-wider text-amber-700 font-medium">
        {I18n.t("projects.avenants.summary.pending_total")}
      </div>
      <div className="text-[18px] font-semibold tabular-nums text-slate-900">{fmt(pendingTotal)}</div>
      <div className="text-[11px] text-amber-700/80 mt-0.5">
        {I18n.t("projects.avenants.summary.pending_count", { count: pendingCount })}
      </div>
    </div>

    <div className="px-4 py-3 bg-blue-50/60 border border-blue-200 rounded-xl">
      <div className="text-[10.5px] uppercase tracking-wider text-blue-700 font-medium">
        {I18n.t("projects.avenants.summary.revised_contract")}
      </div>
      <div className="text-[18px] font-semibold tabular-nums text-slate-900">{fmt(revisedContract)}</div>
      <div className="text-[11px] text-blue-700/80 mt-0.5">
        {I18n.t("projects.avenants.summary.revised_hint")}
      </div>
    </div>
  </div>
)

const AvenantsSection = ({ avenants: initialAvenants, policies }) => {
  const data      = initialAvenants || {}
  const projectId = data.create_url?.match(/\/projects\/(\d+)\/avenants/)?.[1]

  const [items, setItems]                     = useState(data.items || [])
  const [signedTotal, setSignedTotal]         = useState(data.signed_total)
  const [pendingTotal, setPendingTotal]       = useState(data.pending_total)
  const [revisedContract, setRevisedContract] = useState(data.revised_contract)
  const [modalMode, setModalMode]             = useState(null)   // null | "new" | "priced" | "edit"
  const [editTarget, setEditTarget]           = useState(null)
  const canManage = policies?.can_manage_avenants
  const { refresh } = usePanorama()

  const money = cents => ({
    cents,
    formatted: new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(cents / 100),
  })

  const recompute = (nextItems) => {
    const sum = status => nextItems
      .filter(a => a.status === status)
      .reduce((acc, a) => acc + (a.amount?.cents || 0), 0)
    const signedCents  = sum("signed")
    const initialCents = (revisedContract?.cents || 0) - (signedTotal?.cents || 0)
    setItems(nextItems)
    setSignedTotal(money(signedCents))
    setPendingTotal(money(sum("pending")))
    setRevisedContract(money(initialCents + signedCents))
  }

  const handleSaved = (avenant) => {
    const next = items.some(it => it.id === avenant.id)
      ? items.map(it => (it.id === avenant.id ? avenant : it))
      : [...items, avenant]
    recompute(next)
  }

  const act = (promise, successMessage) =>
    promise.then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      handleSaved(response)
      if (successMessage) flashNotice(successMessage)
      refresh()
    })

  const handleSubmitForApproval = (avenant) =>
    act(submitAvenant(projectId, avenant.id),
        I18n.t("projects.avenants.actions.submitted_success", { number: avenant.number }))

  const handleSign = (avenant) => {
    if (!confirm(I18n.t("projects.avenants.actions.confirm_sign", { number: avenant.number }))) return
    act(signAvenant(projectId, avenant.id, null),
        I18n.t("projects.avenants.actions.signed_success", { number: avenant.number }))
  }

  const handleRefuse = (avenant) => {
    if (!confirm(I18n.t("projects.avenants.actions.confirm_refuse", { number: avenant.number }))) return
    act(refuseAvenant(projectId, avenant.id))
  }

  const handleCancel = (avenant) => {
    if (!confirm(I18n.t("projects.avenants.actions.confirm_cancel", { number: avenant.number }))) return
    act(cancelAvenant(projectId, avenant.id))
  }

  const handleDelete = (avenant) => {
    if (!confirm(I18n.t("projects.avenants.actions.confirm_destroy", { number: avenant.number }))) return
    destroyAvenant(projectId, avenant.id).then(response => {
      if (response?.success) recompute(items.filter(it => it.id !== avenant.id))
    })
  }

  const openModal = (mode, target = null) => { setEditTarget(target); setModalMode(mode) }

  const sorted = useMemo(() => [...items].sort((a, b) => a.number.localeCompare(b.number)), [items])

  return (
    <section id="section-avenants" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.avenants.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{I18n.t("projects.avenants.subtitle")}</p>
        </div>
        {/* Une seule porte d'entrée. Un avenant se chiffre comme le marché, dans
            l'éditeur de devis : deux boutons, c'était deux façons de faire la
            même chose et un utilisateur qui hésite. */}
        {canManage && (
          <ActionButton onClick={() => openModal("priced")}>
            {I18n.t("projects.avenants.actions.new")}
          </ActionButton>
        )}
      </header>

      <div className="mb-4">
        <Totals
          signedTotal     = {signedTotal}
          signedCount     = {items.filter(a => a.status === "signed").length}
          pendingTotal    = {pendingTotal}
          pendingCount    = {items.filter(a => a.status === "pending").length}
          revisedContract = {revisedContract}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className={`${COLUMNS} px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium`}>
          <div>{I18n.t("projects.avenants.table.number")}</div>
          <div>{I18n.t("projects.avenants.table.title")}</div>
          <div>{I18n.t("projects.avenants.table.lot")}</div>
          <div className="text-right">{I18n.t("projects.avenants.table.amount")}</div>
          <div className="text-right">{I18n.t("projects.avenants.table.delay")}</div>
          <div>{I18n.t("projects.avenants.table.signed_date")}</div>
          <div>{I18n.t("projects.avenants.table.status")}</div>
          <div className="text-right">{I18n.t("projects.avenants.table.actions")}</div>
        </div>

        {sorted.length === 0 ? (
          <div className="px-6 py-10 text-center text-[13px] text-slate-500">
            {I18n.t("projects.avenants.empty")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sorted.map(avenant => (
              <div key={avenant.id} className={`${COLUMNS} px-4 py-3 items-center hover:bg-slate-50/60 text-[12.5px]`}>
                <div className="font-mono text-slate-700">{avenant.number}</div>

                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-slate-900 font-medium truncate" title={avenant.title}>{avenant.title}</span>
                    {avenant.priced && (
                      <FontAwesomeIcon
                        icon      = "calculator"
                        title     = {I18n.t("projects.avenants.table.priced_hint")}
                        className = "text-[10px] text-blue-500 shrink-0"
                      />
                    )}
                  </div>
                  {avenant.reason_label && (
                    <div className="text-[11px] text-slate-500 mt-0.5">{avenant.reason_label}</div>
                  )}
                  {avenant.description && (
                    <div className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-2">{avenant.description}</div>
                  )}
                </div>

                <div className="min-w-0 text-slate-600 truncate" title={avenant.lot?.title || ""}>
                  {avenant.lot?.title || <span className="text-slate-300">—</span>}
                </div>

                <div className="text-right tabular-nums text-slate-900 font-medium">{fmt(avenant.amount)}</div>

                <div className="text-right tabular-nums text-slate-600">
                  {avenant.delay_days > 0
                    ? I18n.t("projects.avenants.table.delay_days", { count: avenant.delay_days })
                    : <span className="text-slate-300">—</span>}
                </div>

                <div className="text-slate-600">
                  {dateFmt(avenant.signed_date) || (avenant.status === "pending" ? dateFmt(avenant.sent_date) : "")}
                </div>

                <div>
                  <Badge tone={STATUS_TONE[avenant.status] || "neutral"} variant="soft" size="sm">
                    {avenant.status_label}
                  </Badge>
                </div>

                <div className="flex items-center justify-end gap-0.5">
                  {avenant.builder_url && (
                    <a
                      href      = {avenant.builder_url}
                      title     = {I18n.t("projects.avenants.actions.open_quote")}
                      className = "inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <FontAwesomeIcon icon="calculator" />
                    </a>
                  )}
                  {avenant.pdf_url && (
                    <a
                      href      = {avenant.pdf_url}
                      target    = "_blank"
                      rel       = "noopener noreferrer"
                      title     = {I18n.t("projects.avenants.pdf.print")}
                      className = "inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <FontAwesomeIcon icon="file-pdf" />
                    </a>
                  )}
                  {canManage && avenant.status === "draft" && (
                    <IconButton size="md" title={I18n.t("projects.avenants.actions.submit")} onClick={() => handleSubmitForApproval(avenant)}>
                      <FontAwesomeIcon icon="paper-plane" />
                    </IconButton>
                  )}
                  {canManage && ["draft", "pending"].includes(avenant.status) && (
                    <IconButton size="md" title={I18n.t("projects.avenants.actions.sign")} onClick={() => handleSign(avenant)}>
                      <FontAwesomeIcon icon="signature" />
                    </IconButton>
                  )}
                  {canManage && avenant.status === "pending" && (
                    <IconButton size="md" title={I18n.t("projects.avenants.actions.refuse")} onClick={() => handleRefuse(avenant)}>
                      <FontAwesomeIcon icon="thumbs-down" />
                    </IconButton>
                  )}
                  {canManage && !["cancelled", "refused"].includes(avenant.status) && (
                    <IconButton size="md" title={I18n.t("projects.avenants.actions.edit")} onClick={() => openModal("edit", avenant)}>
                      <FontAwesomeIcon icon="pen" />
                    </IconButton>
                  )}
                  {canManage && ["draft", "pending"].includes(avenant.status) && (
                    <IconButton size="md" title={I18n.t("projects.avenants.actions.cancel")} onClick={() => handleCancel(avenant)}>
                      <FontAwesomeIcon icon="ban" />
                    </IconButton>
                  )}
                  {canManage && ["draft", "refused"].includes(avenant.status) && (
                    <IconButton tone="danger" size="md" title={I18n.t("projects.avenants.actions.destroy")} onClick={() => handleDelete(avenant)}>
                      <FontAwesomeIcon icon="trash" />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AvenantModal
        isOpen    = {modalMode != null}
        mode      = {modalMode}
        projectId = {projectId}
        avenant   = {editTarget}
        lots      = {data.lots || []}
        reasons   = {data.reasons || []}
        onClose   = {() => { setModalMode(null); setEditTarget(null) }}
        onSaved   = {handleSaved}
      />
    </section>
  )
}

export default AvenantsSection
